import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollManager — resets scroll position to top on FORWARD navigation.
 *
 * Behavior
 * --------
 * • PUSH / REPLACE (forward navigation, or a direct URL load): scroll to
 *   (0, 0) so the new page opens at the top. Coaches never land partway
 *   down a nested detail page.
 *
 * • POP (browser Back / Forward buttons + Back links that call navigate(-1)):
 *   do NOT reset scroll. The browser's built-in scroll restoration will
 *   put the user back where they were on the previous page — which is
 *   the mobile/desktop UX the user requested.
 *
 * Notes
 * -----
 * • This component renders nothing. Mount it once inside <BrowserRouter>
 *   above <Routes> so every route change is observed.
 * • React Router v6 doesn't do this by default; StackOverflow and the
 *   official docs both point to a small component like this.
 * • Uses `location.key` (not just pathname) so a link that navigates to
 *   the same path with different query params (e.g. Payments status tab
 *   changes) also triggers a scroll reset. Skip that if we ever want tab
 *   switches to preserve scroll — for now, tab switches should also open
 *   at the top for consistency.
 * • The `<ScrollToTop />` alias is exported for readability at the call
 *   site; both names point to the same component.
 */
export default function ScrollManager() {
  const { key } = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"

  useEffect(() => {
    if (navType === "POP") return; // let the browser restore
    // instant, not smooth — smooth-scroll on route change causes a
    // visible flicker on iOS Safari and Chrome mobile.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      // Older browsers reject `behavior: "instant"` — use the two-arg form.
      try { window.scrollTo(0, 0); } catch { /* noop */ }
    }
  }, [key, navType]);

  return null;
}

export const ScrollToTop = ScrollManager;
